import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, BellRing, BellOff, Copy, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("elm_admin_token") || ""}` }, withCredentials: true });
const errMsg = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
const btn = "px-4 py-2 rounded-full border border-wood/25 text-xs text-bark hover:border-terra hover:text-terra transition-colors disabled:opacity-40 inline-flex items-center gap-1.5";
const d = (iso) => new Intl.DateTimeFormat("fr-CH", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso));

export const AdminTelegram = () => {
  const [etat, setEtat] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [apercu, setApercu] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/telegram/etat`, authHeaders()).then((r) => setEtat(r.data)).catch(() => setEtat({ configure: false }));
    axios.get(`${API}/events`).then((r) => setEvents(r.data.filter((e) => !["voyage", "salon"].includes(e.type)))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!eventId) { setApercu(null); return; }
    axios.get(`${API}/admin/telegram/apercu/${eventId}`, authHeaders()).then((r) => setApercu(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [eventId]);

  const run = async (fn, ok) => { setBusy(true); try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r.data) : ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const copier = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success("Texte copié"));

  if (!etat) return <Loader2 className="w-5 h-5 animate-spin text-fumee" />;
  const Icon = etat.configure ? BellRing : BellOff;
  return (
    <div className="space-y-8" data-testid="admin-telegram">
      <div className="bg-card border border-sand-dark rounded-2xl p-5 grid sm:grid-cols-[auto_1fr_auto] gap-4 items-start" data-testid="admin-telegram-etat">
        <Icon className={`w-5 h-5 mt-0.5 ${etat.configure ? "text-sage" : "text-terra"}`} />
        <div className="text-sm text-bark">
          <p className="font-serif text-lg">Notifications Telegram — {etat.configure ? "actives" : "non configurées"}</p>
          <p className="text-xs text-fumee mt-1">
            {etat.configure
              ? <>Chat {etat.chat_id} · alertes J−{etat.jours_avant} et récapitulatif J−0 chaque jour à {etat.heure_alertes} · notification instantanée à chaque réservation, commande ou carte cadeau validée (jamais deux fois pour la même référence).</>
              : <>Renseignez <code>TELEGRAM_BOT_TOKEN</code>{etat.chat_id ? "" : <> et <code>TELEGRAM_CHAT_ID</code></>} dans les variables d'environnement sécurisées du backend, puis redémarrez. Le token n'est jamais affiché ni envoyé au navigateur.</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button disabled={busy || !etat.configure} onClick={() => run(() => axios.post(`${API}/admin/telegram/test`, {}, authHeaders()), "Message de test envoyé sur Telegram")} data-testid="admin-telegram-test" className={btn}><Send className="w-3.5 h-3.5" /> Tester</button>
          <button disabled={busy || !etat.configure} onClick={() => run(() => axios.post(`${API}/admin/telegram/recap`, {}, authHeaders()), (r) => (r.vide ? "Rien aujourd'hui — aucun message envoyé" : "Récapitulatif du jour envoyé"))} data-testid="admin-telegram-recap" className={btn}>Récap du jour</button>
        </div>
      </div>

      <div>
        <p className="eyebrow mb-3">Aperçu de l'alerte J−{etat.jours_avant}</p>
        <select value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="admin-telegram-event" className="w-full sm:max-w-xl bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra">
          <option value="">Choisir un événement à venir…</option>
          {events.map((e) => <option key={e.id} value={e.id}>{d(e.date)} — {e.titre}</option>)}
        </select>
        {apercu && (
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <div className="bg-card border border-sand-dark rounded-2xl p-5" data-testid="admin-telegram-apercu">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee mb-3">Message Telegram · {apercu.inscrits} inscription{apercu.inscrits > 1 ? "s" : ""}</p>
              <div className="text-sm text-bark whitespace-pre-wrap break-words [&_pre]:bg-sand/60 [&_pre]:rounded-xl [&_pre]:p-3 [&_pre]:text-xs [&_pre]:mt-2 [&_a]:underline" dangerouslySetInnerHTML={{ __html: apercu.telegram_html }} />
              <div className="flex flex-wrap gap-2 mt-4">
                <button disabled={busy || !etat.configure} onClick={() => run(() => axios.post(`${API}/admin/telegram/envoyer/${eventId}`, {}, authHeaders()), "Alerte envoyée sur Telegram")} data-testid="admin-telegram-envoyer" className={btn}><Send className="w-3.5 h-3.5" /> Envoyer maintenant</button>
                {apercu.numeros && <button onClick={() => copier(apercu.numeros)} data-testid="admin-telegram-copier-numeros" className={btn}><Copy className="w-3.5 h-3.5" /> Copier les numéros</button>}
              </div>
            </div>
            <div className="bg-earth text-sand rounded-2xl p-5" data-testid="admin-telegram-whatsapp">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand/60 mb-3">Texte WhatsApp prêt à copier</p>
              <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed" data-testid="admin-telegram-whatsapp-texte">{apercu.whatsapp}</pre>
              <button onClick={() => copier(apercu.whatsapp)} data-testid="admin-telegram-copier-whatsapp" className="mt-4 px-4 py-2 rounded-full bg-terra text-earth text-xs inline-flex items-center gap-1.5 hover:bg-sand transition-colors"><Copy className="w-3.5 h-3.5" /> Copier le message</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
